import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from './Products/ProductCard';
import Loader from './Loader';
import SortDropdown from './Products/SortDropdown';
import { FaArrowRight, FaLeaf } from 'react-icons/fa';
import useStore from '../store/useStore';
import { buildApiUrl } from '../utils/api';
import { normalizeProductCard } from '../utils/normalizeProductCard';
import { getActivePincode } from '../utils/location';

const ProductGridSkeleton = () => (
  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-6">
    {[...Array(10)].map((_, i) => (
      <div key={i} className="animate-pulse rounded-3xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="aspect-square bg-gray-200/70 w-full" />
        <div className="p-4 space-y-3">
          <div className="h-3 w-20 rounded-full bg-gray-200" />
          <div className="h-4 w-full rounded-full bg-gray-200" />
          <div className="h-4 w-3/4 rounded-full bg-gray-200" />
          <div className="h-5 w-1/2 rounded-full bg-gray-200" />
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div className="h-9 rounded-xl bg-gray-200" />
            <div className="h-9 rounded-xl bg-gray-200" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ProductGrid = () => {
  // Own local state - completely independent of the shared Zustand products store.
  // This prevents Home page fetches from polluting the store and flashing on ProductsPage.
  const [products, setProducts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [sortBy, setSortBy]             = useState('default');
  const [visibleCount, setVisibleCount] = useState(12);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const loadMoreTimerRef = useRef(null);

  const navigate = useNavigate();
  const activePincode = useStore((s) => getActivePincode(s));

  useEffect(() => {
    let alive = true;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: '1', limit: '100' });
        if (activePincode) {
          params.set('pincode', String(activePincode));
        }
        const res  = await fetch(buildApiUrl(`/api/v1/customer/products/list?${params.toString()}`));
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error();
        const raw = json.data?.products ?? json.data ?? [];
        if (alive) setProducts(raw.map(normalizeProductCard));
      } catch {
        if (alive) setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchAll();
    return () => { alive = false; };
  }, [activePincode]);

  const sortedProducts = React.useMemo(() => {
    return [...products].sort((a, b) => {
      if (sortBy === 'price-low')  return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'discount')   return b.discount - a.discount;
      if (sortBy === 'name')       return (a.name || '').localeCompare(b.name || '');
      return 0;
    });
  }, [products, sortBy]);

  const visibleProducts = sortedProducts.slice(0, visibleCount);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (
      loading ||
      !sentinel ||
      visibleCount >= sortedProducts.length ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting) || loadingMoreRef.current) {
        return;
      }

      loadingMoreRef.current = true;
      setIsLoadingMore(true);
      observer.unobserve(sentinel);
      loadMoreTimerRef.current = window.setTimeout(() => {
        setVisibleCount((current) => Math.min(current + 12, sortedProducts.length));
        loadingMoreRef.current = false;
        setIsLoadingMore(false);
      }, 180);
    }, { rootMargin: '240px 0px', threshold: 0 });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, sortedProducts.length, visibleCount]);

  useEffect(() => () => window.clearTimeout(loadMoreTimerRef.current), []);

  return (
    <section className="bg-white py-10 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
              Popular Picks
            </span>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-gray-900 sm:text-4xl">
              Explore All Products
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Showing {visibleProducts.length} of {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Sort dropdown */}
          <SortDropdown value={sortBy} onChange={setSortBy} />
        </div>

        {/* States */}
        {loading ? (
          <>
            <Loader compact label="Loading products" sublabel="Checking the latest product availability." />
            <ProductGridSkeleton />
          </>
        ) : visibleProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-gray-200 bg-white py-24 text-center">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-50 text-primary-500">
              <FaLeaf className="h-9 w-9" aria-hidden="true" />
            </span>
            <h3 className="text-xl font-bold text-gray-900">No products found</h3>
            <p className="text-sm text-gray-500">Try adjusting your filters or browse all categories.</p>
            <button
              onClick={() => navigate('/products')}
              className="mt-2 rounded-full border-2 border-primary-500 px-6 py-2.5 text-sm font-bold text-primary-600 hover:bg-primary-50"
            >
              Browse All
            </button>
          </div>
        ) : (
          <>
            {/* Mobile hint */}
            <div className="mb-4 flex items-center justify-between sm:hidden">
              <p className="text-xs text-gray-400">Showing {visibleProducts.length} of {sortedProducts.length}</p>
              <button onClick={() => navigate('/products')} className="text-xs font-bold text-primary-600">
                <span className="inline-flex items-center gap-1">
                  View all
                  <FaArrowRight className="h-3 w-3" aria-hidden="true" />
                </span>
              </button>
            </div>

            {/* Product grid */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-6">
              {visibleProducts.map((product) => (
                <ProductCard key={product.id ?? product.product_id} product={product} />
              ))}
            </div>

            {/* Scroll sentinel: append the next batch as the user approaches the end. */}
            {visibleCount < sortedProducts.length && (
              <div
                ref={loadMoreRef}
                className="mt-8 flex min-h-10 items-center justify-center"
                aria-live="polite"
              >
                {isLoadingMore && (
                  <div role="status" className="inline-flex items-center gap-2 text-xs font-medium text-gray-500">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
                    Loading more products...
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ProductGrid;
